import { useConfigStore, useDojoContext, useGameStore, useRouterContext } from "@/dojo/hooks";

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Heading,
  ListItem,
  ListItemProps,
  StyleProps,
  Text,
  UnorderedList,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { ReactNode, useRef } from "react";
import { Cigarette, Clock, Close, Dots, GangIcon, Home, PaperIcon, Refresh } from "../icons";
import { Calendar } from "../icons/archive";
import { HeaderButton, MediaPlayer } from ".";
import { ChainSelector, ConnectButton, TokenBalance } from "../wallet";

import { ProfileLinkDrawer } from "../pages/profile/Profile";
import { useAccount } from "@starknet-react/core";

import colors from "@/theme/colors";
import { HustlerIcon, Hustlers } from "../hustlers";

const slideAnim = keyframes`  
  0% {transform: translateX(0); color: ${colors.neon["500"]};}
  65% {transform: translateX(-125%); color: ${colors.neon["500"]};}
  80% {transform: translateX(-97%); color: ${colors.yellow["500"]};}
  82% {transform: translateX(-97%); color: ${colors.neon["500"]};}
  92% {transform: translateX(-105%); color: ${colors.neon["500"]};}
  100% {transform: translateX(-100%); ; color: ${colors.neon["400"]};}
`;

const DrawerMenu = () => {
  const { router, gameId, isAdmin } = useRouterContext();
  const { game } = useGameStore();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const { config } = useConfigStore();
  const { account } = useAccount();
  const { uiStore } = useDojoContext();
  return (
    <>
      <HeaderButton ref={btnRef} onClick={onOpen} h={["40px", "48px"]} w={["40px", "48px"]}>
        <Dots />
      </HeaderButton>
      <Drawer isOpen={isOpen} placement="right" size="xs" onClose={onClose} trapFocus={false}>
        <DrawerOverlay onClick={onClose} />
        <DrawerContent minW="340px" overflowX="hidden">
          <DrawerHeader borderBottom="solid 2px" borderColor="neon.900" pb={2}>
            <Heading
              color="neon.500"
              fontWeight="normal"
              position="relative"
              left="100%"
              animation={`${slideAnim} .6s ease-out 0.1s 1 normal forwards`}
              willChange="transform"
            >
              <PaperIcon width="28px" height="28px" />
            </Heading>
          </DrawerHeader>
          <Close position="absolute" right="16px" top="16px" cursor="pointer" onClick={onClose} />
          <DrawerBody px={3} py={0}>
            <VStack
              w="full"
              h="full"
              justifyContent="space-between"
              // alignItems="flex-start"
              gap={6}
            >
              <VStack w="full" alignItems="flex-start" gap={12}>
                <UnorderedList w="full" listStyleType="none" marginInlineStart="0">
                  <DrawerListItem cursor="default">
                    <MediaPlayer />
                  </DrawerListItem>

                  <DrawerListItem cursor="default">
                    <Text color="neon.500"> ACCOUNT </Text>
                  </DrawerListItem>

                  <DrawerListItem cursor="default">
                    <HStack w="full" justifyContent="space-between">
                      <ConnectButton />
                      {process.env.NODE_ENV !== "production" && (
                        <>
                          <ChainSelector canChange={!gameId} />
                        </>
                      )}
                    </HStack>
                  </DrawerListItem>

                  {account && config && (
                    <DrawerListItem cursor="default">
                      <HStack w="full">
                        <TokenBalance address={account?.address} token={config?.ryoAddress.paper} icon={PaperIcon} />{" "}
                        <Text>PAPER</Text>
                      </HStack>
                    </DrawerListItem>
                  )}

                  {game && (
                    <>
                      <DrawerListItem cursor="default">
                        <HStack w="full" justifyContent="space-between">
                          <Text color="neon.500"> GAME </Text>
                          <></>
                        </HStack>
                      </DrawerListItem>

                      <DrawerListItem>
                        <ProfileLinkDrawer />
                      </DrawerListItem>

                      <DrawerListItem
                        onClick={() => {
                          uiStore.openRefreshGame();
                        }}
                      >
                        <Refresh mr={2} /> REFRESH
                      </DrawerListItem>
                    </>
                  )}

                  <DrawerListItem cursor="default" color="neon.500">
                    <Text> NAVIGATION </Text>
                  </DrawerListItem>

                  <DrawerListItem
                    onClick={() => {
                      router.push("/");
                    }}
                  >
                    <Home mr={2} /> HOME
                  </DrawerListItem>

                  {account && (
                    <DrawerListItem
                      onClick={() => {
                        router.push(`/game/history/0x${BigInt(account.address || 0).toString(16)}`);
                      }}
                    >
                      <Clock mr={2} /> HISTORY
                    </DrawerListItem>
                  )}
                  <DrawerListItem
                    onClick={() => {
                      router.push("/season");
                    }}
                  >
                    <Calendar mr={2} /> SEASONS
                  </DrawerListItem>

                  <DrawerListItem
                    onClick={() => {
                      router.push("/gear");
                    }}
                  >
                    <Cigarette mr={2} /> GEAR
                  </DrawerListItem>

                  <DrawerListItem
                    onClick={() => {
                      router.push("/dope");
                    }}
                  >
                    <HustlerIcon hustler={Hustlers.Dragon} mr={2} /> MY DOPE
                  </DrawerListItem>

                  <DrawerListItem
                    onClick={() => {
                      router.push("/dope/votes");
                    }}
                  >
                    <GangIcon mr={2} /> MY VOTES
                  </DrawerListItem>

                  {/* DEV */}

                  {isAdmin && (
                    <>
                      <DrawerListItem cursor="default" color="neon.500">
                        <Text> ADMIN </Text>
                      </DrawerListItem>
                      <DrawerListItem
                        onClick={() => {
                          router.push("/admin");
                        }}
                      >
                        <Cigarette mr={2} /> ADMIN
                      </DrawerListItem>
                      <DrawerListItem
                        onClick={() => {
                          router.push("/devtools");
                        }}
                      >
                        <Cigarette mr={2} /> DEVTOOLS
                      </DrawerListItem>
                    </>
                  )}
                </UnorderedList>
              </VStack>

              {/* <MediaPlayer /> */}
            </VStack>
          </DrawerBody>

          <DrawerFooter>{/* <BuiltBy /> */}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default DrawerMenu;

const DrawerListItem = ({ children, ...props }: { children: ReactNode } & StyleProps & ListItemProps) => {
  return (
    <ListItem
      display="flex"
      alignItems="center"
      borderBottom="solid 2px"
      borderColor="neon.900"
      pb={2}
      pt={2}
      cursor="pointer"
      minH="48px"
      {...props}
    >
      {children}
    </ListItem>
  );
};
